/**
 * Campaign markers on time-series charts (HOME-04): campaigns sent in the
 * period grouped by chart bucket, the label above a marker and the send time
 * shown in the bucket's card.
 */
import { DateTime } from 'luxon';
import type { Locales, TranslationFunctions } from '@/i18n/i18n-types';
import { bucketStartIso, type ChartGranularity } from '@/lib/chart-axis';
import { formatDayMonth, type ChartRange } from '@/lib/partial-buckets';

export interface MarkerCampaign {
  name: string;
  launchDateTime: string;
}

const MAX_LABEL_LENGTH = 20;

/**
 * Campaigns launched within `range`, keyed by `bucketStartIso` (the same key
 * as the chart rows), oldest first inside each bucket.
 */
// SPEC_DEVIATION: takes `range` (design: `(campaigns, granularity, locale)`).
// Reason: campaigns outside the period must not get a marker.
export function groupCampaignsByBucket<C extends MarkerCampaign>(
  campaigns: C[],
  granularity: ChartGranularity,
  locale: Locales,
  range: ChartRange
): Map<string, C[]> {
  const from = DateTime.fromISO(range.from);
  const to = DateTime.fromISO(range.to);
  const sorted = campaigns
    .map((campaign) => ({
      campaign,
      launch: DateTime.fromISO(campaign.launchDateTime),
    }))
    .filter(({ launch }) => launch.isValid && launch >= from && launch <= to)
    .sort((a, b) => a.launch.toMillis() - b.launch.toMillis());

  const groups = new Map<string, C[]>();
  for (const { campaign } of sorted) {
    const key = bucketStartIso(campaign.launchDateTime, granularity, locale);
    if (key === null) continue;
    groups.set(key, [...(groups.get(key) ?? []), campaign]);
  }
  return groups;
}

/** Name up to 20 characters + "…", or "{N} campanhas" for 2+. */
export function markerLabel(
  list: MarkerCampaign[],
  LL: TranslationFunctions
): string {
  if (list.length >= 2) return LL.chartCampaigns.count({ count: list.length });
  const chars = [...(list[0]?.name ?? '')];
  return chars.length > MAX_LABEL_LENGTH
    ? `${chars.slice(0, MAX_LABEL_LENGTH).join('')}…`
    : chars.join('');
}

/** A desktop marker label's box, in chart pixels. */
export interface MarkerLabelBox {
  /** Row index of the bucket (rows are in time order). */
  index: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** Least horizontal room between two visible labels. */
const LABEL_GAP = 4;

/**
 * Labels that stay visible when some would overlap (HOME-04): going from the
 * most recent bucket back, a label is hidden when it comes closer than 4px to
 * one already kept. Returns the row indexes of the visible labels.
 */
export function visibleMarkerLabels(
  boxes: readonly MarkerLabelBox[]
): Set<number> {
  const kept: MarkerLabelBox[] = [];
  for (const box of [...boxes].sort((a, b) => b.index - a.index)) {
    const overlaps = kept.some(
      (other) =>
        box.left < other.right + LABEL_GAP &&
        other.left < box.right + LABEL_GAP &&
        box.top < other.bottom &&
        other.top < box.bottom
    );
    if (!overlaps) kept.push(box);
  }
  return new Set(kept.map((box) => box.index));
}

/** "HH:mm" by hour/day; "{d} {mmm}, HH:mm" by week/month. */
export function markerTime(
  launchIso: string,
  granularity: ChartGranularity,
  locale: Locales
): string {
  const launch = DateTime.fromISO(launchIso);
  const time = launch.toFormat('HH:mm');
  return granularity === 'week' || granularity === 'month'
    ? `${formatDayMonth(launch, locale)}, ${time}`
    : time;
}
